// Pure helpers behind the MSP billing panel — period list and CSV assembly.
// Kept out of the component so they can be tested without rendering, matching
// the msp-list.ts split.

import type { MspBillingChild, MspTenant } from "@/types/api";

/** Billing periods as `YYYY-MM`, newest first, ending at `from`'s month. */
export function recentPeriods(from: Date, count = 12): string[] {
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(from.getFullYear(), from.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

export function formatPeriod(period: string): string {
  const [year, month] = period.split("-").map(Number);
  if (!year || !month) return period;
  return new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

export function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  const exp = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** exp;
  return `${value.toFixed(exp === 0 ? 0 : 1)} ${units[exp]}`;
}

/**
 * RFC 4180 field, with spreadsheet formula injection defused.
 *
 * Client names are customer-supplied at onboarding and this CSV is opened in
 * Excel by the MSP, so a name beginning `=`, `+`, `-`, `@` or a control char
 * would execute on open. Prefixing with an apostrophe makes the cell literal;
 * the leading `'` then forces quoting so the guard cannot be stripped by a
 * naive re-parse.
 */
function csvField(value: string | number): string {
  const s = String(value ?? "");
  const guarded = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\r\n']/.test(guarded)
    ? `"${guarded.replace(/"/g, '""')}"`
    : guarded;
}

const CSV_HEADER = [
  "client",
  "slug",
  "plan",
  "period",
  "endpoints",
  "events",
  "alerts",
  "storage_bytes",
];

/**
 * Usage rows an MSP can hand to their own invoicing. Plan comes from the tenant
 * list rather than the billing payload — the MSP needs the rate card entry to
 * price the line, and the usage endpoint doesn't carry it.
 */
export function billingCsv(
  children: MspBillingChild[],
  tenants: MspTenant[],
  period: string,
): string {
  const planById = new Map(tenants.map((t) => [t.id, t.plan]));
  const lines = [CSV_HEADER.join(",")];
  for (const c of children) {
    lines.push(
      [
        csvField(c.name),
        csvField(c.slug),
        csvField(planById.get(c.tenant_id) ?? ""),
        csvField(period),
        c.endpoints,
        c.events,
        c.alerts,
        c.storage_bytes,
      ].join(","),
    );
  }
  return `${lines.join("\n")}\n`;
}
